import { deleteScheduledActivities } from '@kizen/api/activities';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';
import { useActivityRulesContext } from 'ts-activities/ActivityRulesContext';

import useModal from 'components/Modals/useModal';
import ActivityBuilderPreview from 'components/Modals/ActivityBuilderPreview';
import { DesktopOnlyButton } from 'pages/Common/styles/ControlBar';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import ConfirmNavigationModal from 'components/Modals/presets/ConfirmNavigation';
import ActivityService from 'services/ActivityService';
import { toastVariant, useToast } from 'components/ToastProvider';
import { useActivitySharingContext } from 'ts-activities/ActivitySharingContext';
import AxiosService, { getOriginalError } from 'services/AxiosService';
import { useMemo, useState } from 'react';
import { DeleteActivityModal } from './DisableActivityModal';
import { Nav } from 'components/Navigation/NavLayout';
import { TABS } from './pages/ScheduledAndLogged/constants';

export const NAVIGATE_AWAY_MESSAGE = (t) =>
  t('Please name your activity prior to leaving this page.');

export const NAVIGATE_AWAY_URL_MESSAGE = (t) =>
  t('Please enter a valid URL prior to leaving this page.');

export const ControlBarControl = ({
  activity,
  setErrorMessages,
  name,
  ...rest
}) => {
  const { subpage } = useParams();
  if (subpage === 'rules') {
    return <RulesControlBar />;
  }
  if (subpage === 'team-sharing-settings') {
    return <TeamSharingSettingsControlBar activity={activity} />;
  }
  if (subpage === 'scheduled-and-logged') {
    return <ScheduledAndLoggedControlBar activity={activity} {...rest} />;
  }
  return (
    <ControlBar
      name={name}
      setErrorMessages={setErrorMessages}
      activity={activity}
    />
  );
};

export const ControlBar = ({ activity, setErrorMessages }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [showPreview, togglePreview] = useToggle(false);

  const [confirmDeleteModalProps, , confirmDeleteModal] = useModal({
    handleSubmit: async () => {
      await ActivityService.v2DeleteActivity({
        activityObjectId: activity.id,
      });
      setErrorMessages({});
      history.replace('/activities');
    },
  });

  return (
    <>
      <Nav.TopNavLeftContainer>
        <Nav.TopNavBackButton
          label={t('back to activities')}
          path="/activities"
        />
      </Nav.TopNavLeftContainer>
      <Nav.TopNavCenterContainer>
        <Nav.TopNavHeader label={activity.name} />
      </Nav.TopNavCenterContainer>
      <Nav.TopNavRightContainer>
        <DesktopOnlyButton onClick={confirmDeleteModal.show} color="red">
          {t('Delete')}
        </DesktopOnlyButton>
        <DesktopOnlyButton color="blue" onClick={togglePreview}>
          {t('Preview')}
        </DesktopOnlyButton>
      </Nav.TopNavRightContainer>
      <ConfirmDeletionModal {...confirmDeleteModalProps}>
        {t('This will permanently delete the Activity.')}
      </ConfirmDeletionModal>
      {showPreview && (
        <ActivityBuilderPreview
          activity={activity}
          onHide={togglePreview}
          actionBtnColor="blue"
        />
      )}
    </>
  );
};
export const RulesControlBar = () => {
  const { t } = useTranslation();
  const { activity, enableSaveButton, hasEdits, handleSave } =
    useActivityRulesContext();
  const [showPreview, togglePreview] = useToggle(false);

  const handleSaveAndPreview = async () => {
    if (await handleSave()) {
      togglePreview();
    }
  };

  return (
    <>
      <Nav.TopNavLeftContainer>
        <Nav.TopNavBackButton
          label={t('back to activities')}
          path="/activities"
        />
      </Nav.TopNavLeftContainer>

      <Nav.TopNavCenterContainer>
        <Nav.TopNavHeader label={activity.name} />
      </Nav.TopNavCenterContainer>
      <Nav.TopNavRightContainer>
        <DesktopOnlyButton
          color="green"
          disabled={!enableSaveButton}
          onClick={handleSave}
        >
          {t('Save')}
        </DesktopOnlyButton>
        <DesktopOnlyButton
          color="blue"
          disabled={!enableSaveButton}
          onClick={handleSaveAndPreview}
        >
          {`${t('Save & Preview')}`}
        </DesktopOnlyButton>
      </Nav.TopNavRightContainer>
      <ConfirmNavigationModal
        when={hasEdits}
        whenCondition={({ location }) => !location.pathname.endsWith('rules')}
      />
      {showPreview && (
        <ActivityBuilderPreview
          activity={activity}
          onHide={togglePreview}
          actionBtnColor="blue"
        />
      )}
    </>
  );
};
export const TeamSharingSettingsControlBar = ({ activity }) => {
  const { t } = useTranslation();
  const { hasEdits, handleSave, saving } =
    useActivitySharingContext();
  const [showPreview, togglePreview] = useToggle(false);

  return (
    <>
      <Nav.TopNavLeftContainer>
        <Nav.TopNavBackButton
          label={t('back to activities')}
          path="/activities"
        />
      </Nav.TopNavLeftContainer>

      <Nav.TopNavCenterContainer>
        <Nav.TopNavHeader label={activity.name} />
      </Nav.TopNavCenterContainer>
      <Nav.TopNavRightContainer>
        <DesktopOnlyButton color="green" onClick={handleSave} loading={saving}>
          {t('Save')}
        </DesktopOnlyButton>
        <DesktopOnlyButton color="blue" onClick={togglePreview}>
          {t('Preview')}
        </DesktopOnlyButton>
      </Nav.TopNavRightContainer>
      <ConfirmNavigationModal
        when={hasEdits}
        whenCondition={({ location }) =>
          !location.pathname.endsWith('team-sharing-settings')
        }
      />
      {showPreview && (
        <ActivityBuilderPreview
          activity={activity}
          onHide={togglePreview}
          actionBtnColor="blue"
        />
      )}
    </>
  );
};

export const ScheduledAndLoggedControlBar = ({
  activity,
  customLayoutSelectedTab,
}) => {
  const [showToast] = useToast();
  const { t } = useTranslation();
  const { hasEdits } = useActivitySharingContext();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleExportResponses = async ({ id: activityObjectId, name }) => {
    switch (customLayoutSelectedTab) {
      case TABS.scheduled:
        try {
          await ActivityService.v2ExportScheduledActivities({
            activityObjectId,
          });
          const message = t(
            `Scheduled activities were queued for export successfully. You will be emailed a download link once complete`
          );
          showToast({
            variant: toastVariant.SUCCESS,
            message,
          });
        } catch (error) {
          const orig = getOriginalError(error);
          const message =
            orig?.message ||
            t(
              'The export was not successful. Please try again or contact Kizen support.'
            );
          showToast({
            variant: toastVariant.FAILURE,
            message,
          });
        }
        return;
      case TABS.logged:
        try {
          await ActivityService.v2ExportActivities({
            activityObjectId,
          });
          const message = t(
            `Responses were queued for export successfully. You will be emailed a download link once complete`
          );
          showToast({
            variant: toastVariant.SUCCESS,
            message,
          });
        } catch (error) {
          const orig = getOriginalError(error);
          const message =
            orig?.message ||
            t(
              'The export was not successful. Please try again or contact Kizen support.'
            );
          showToast({
            variant: toastVariant.FAILURE,
            message,
          });
        }
        return;
      default:
        break;
    }
  };

  const exportButtonTitle = useMemo(() => {
    switch (customLayoutSelectedTab) {
      case TABS.scheduled:
        return t('Export Scheduled');
      case TABS.logged:
        return t('Export Responses');
      default:
        return t('Export Scheduled');
    }
  }, [t, customLayoutSelectedTab]);

  return (
    <>
      <Nav.TopNavLeftContainer>
        <Nav.TopNavBackButton
          label={t('back to activities')}
          path="/activities"
        />
      </Nav.TopNavLeftContainer>

      <Nav.TopNavCenterContainer>
        <Nav.TopNavHeader label={activity.name} />
      </Nav.TopNavCenterContainer>
      <Nav.TopNavRightContainer>
        {customLayoutSelectedTab === TABS.scheduled && (
          <DesktopOnlyButton
            color="red"
            onClick={() => setShowDeleteModal(true)}
          >
            {t('Delete Scheduled')}
          </DesktopOnlyButton>
        )}
        <DesktopOnlyButton
          color="blue"
          onClick={() => handleExportResponses(activity)}
        >
          {exportButtonTitle}
        </DesktopOnlyButton>
      </Nav.TopNavRightContainer>
      {showDeleteModal && (
        <DeleteActivityModal
          activity={activity}
          onHide={() => {
            setShowDeleteModal(false);
          }}
          onConfirm={async ({ sendCalendarSyncEmails }) => {
            await deleteScheduledActivities(AxiosService, activity.id, {
              send_calendar_sync_emails: sendCalendarSyncEmails,
            });
            setShowDeleteModal(false);
          }}
        />
      )}
      <ConfirmNavigationModal
        when={hasEdits}
        whenCondition={({ location }) =>
          !location.pathname.endsWith('scheduled-and-logged')
        }
      />
    </>
  );
};
